import dayjs from 'dayjs';
import type {UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {RadioGroup, RadioGroupItem} from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {DurationType} from '@/services/graphqlHooks';
import durationType from '@/translation/durationType';
import {TIME_FORMATS} from '@/utils/timeFormats';

import TaskTimeInput from '../../../../components/TaskTimeInput';
import type {TaskFormValues} from '../../utils/tasksFormInputs';
import {OccurrencesType, TaskType} from '../../utils/tasksFormInputs';

export type RecurringTaskFormFieldsProps = {
  form: UseFormReturn<TaskFormValues>;
  isStartDateLocked?: boolean;
};

const RecurringTaskFormFields = ({
  form,
  isStartDateLocked,
}: RecurringTaskFormFieldsProps) => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const occurrenceDate = isStartDateLocked ? searchParams.get('date') : null;

  const durationTypeOptions = Object.values(DurationType);

  return (
    <div className="flex flex-col gap-10">
      <div className="flex items-start gap-4">
        <FormField
          control={form.control}
          name="durationAmount"
          render={({field}) => (
            <FormItem>
              <div className="flex items-center gap-4">
                <FormLabel required>{t('Every')}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value?.toString() || ''}
                    onChange={evt => {
                      const value = evt.target.value;
                      field.onChange(
                        value === '' ? '' : parseInt(value, 10) || ''
                      );
                    }}
                    onBlur={evt => {
                      const value = evt.target.value;
                      field.onChange(
                        value === '' ? 1 : parseInt(value, 10) || 1
                      );
                    }}
                    required
                    type="number"
                    min={1}
                    max={100}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="durationType"
          render={({field}) => (
            <FormItem>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('Duration type')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {durationTypeOptions.map(option => (
                      <SelectItem key={option} value={option}>
                        {durationType[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="flex gap-4 max-sm:flex-col">
        <FormField
          control={form.control}
          name="startTimestamp"
          render={({field}) => (
            <FormItem className="grow basis-0">
              <FormLabel required={!isStartDateLocked}>
                {t('Start from')}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={field.onChange}
                  value={
                    occurrenceDate
                      ? dayjs(occurrenceDate).format(TIME_FORMATS.awsDate)
                      : dayjs(field?.value).format(TIME_FORMATS.awsDate)
                  }
                  required
                  disabled={isStartDateLocked}
                  type="date"
                />
              </FormControl>
              {isStartDateLocked && (
                <FormDescription>
                  {t(
                    'The start date cannot be changed after the task is created'
                  )}
                </FormDescription>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {form.watch('taskType') !== TaskType.Announcement && (
          <FormField
            control={form.control}
            name="time"
            render={({field}) => (
              <FormItem className="grow basis-0">
                <FormLabel required>{t('Time')}</FormLabel>
                <FormControl>
                  <TaskTimeInput
                    value={field.value || ''}
                    isHourInterval={
                      form.watch('durationType') === DurationType.Hour
                    }
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
      <FormField
        control={form.control}
        name="occurrencesType"
        render={({field}) => (
          <FormItem>
            <FormControl>
              <RadioGroup
                className="flex flex-col gap-4"
                onValueChange={field.onChange}
                defaultValue={field.value}
              >
                <FormLabel className="mb-2">{t('Ends')}</FormLabel>
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <RadioGroupItem value={OccurrencesType.Never} />
                  </FormControl>
                  <FormLabel>{t('Never')}</FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <RadioGroupItem value={OccurrencesType.After} />
                  </FormControl>
                  <FormLabel>{t('After')}</FormLabel>
                  <FormField
                    control={form.control}
                    name="occurrences"
                    render={({field}) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            {...field}
                            disabled={
                              form.watch('occurrencesType') !==
                              OccurrencesType.After
                            }
                            required
                            value={field.value?.toString() || ''}
                            onChange={evt => {
                              const value = evt.target.value;
                              field.onChange(
                                value === '' ? '' : parseInt(value, 10) || ''
                              );
                            }}
                            onBlur={evt => {
                              const value = evt.target.value;
                              field.onChange(
                                value === '' ? 1 : parseInt(value, 10) || 1
                              );
                            }}
                            type="number"
                            min={1}
                            max={100}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormLabel>{t('Occurrences')}</FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
};

export default RecurringTaskFormFields;
